import { z } from 'zod';
import { ValidationError } from './errors.js';
import { applySizeUpcharges, getDTFPPMargin, round2 } from './helpers.js';
import { DEFAULT_PRICING_CONFIG_BODY } from './pricing-config/defaults.js';
const SizesSchema = z
    .object({
    sxl: z.number().int().min(0),
    xxl: z.number().int().min(0),
    xxxl: z.number().int().min(0),
    xxxxl: z.number().int().min(0).default(0),
    xxxxxl: z.number().int().min(0).default(0),
})
    .refine((s) => s.sxl + s.xxl + s.xxxl + s.xxxxl + s.xxxxxl > 0, {
    message: 'Total quantity across sizes must be greater than zero.',
});
const LocationSchema = z.object({
    label: z.string().min(1).optional(),
    sqin: z.number().positive(),
});
const GarmentSchema = z.discriminatedUnion('source', [
    z.object({
        source: z.literal('we_provide'),
        blankCostEach: z.number().min(0),
    }),
    z.object({
        source: z.literal('customer_provides'),
    }),
]);
export const QuoteDtfPressAndPressInputSchema = z.object({
    sizes: SizesSchema,
    locations: z.array(LocationSchema).min(1),
    garment: GarmentSchema,
    laborPerLocation: z.number().positive().optional(),
    marginPctOverride: z.number().min(0).max(100).optional(),
});
export const QuoteDtfPressAndPressResultSchema = z
    .object({
    totalQty: z.number().int(),
    numLocations: z.number().int(),
    totalSqInPerShirt: z.number(),
    blankCostPerUnit: z.number(),
    sizeUpchargeTotal: z.number(),
    laborPerLocation: z.number(),
    laborCostPerShirt: z.number(),
    transferCostPerShirt: z.number(),
    costPerShirt: z.number(),
    marginPctUsed: z.number(),
    sellPerShirt: z.number(),
    floorPerShirt: z.number(),
    flooredAt: z.boolean(),
    marginPctActual: z.number(),
    orderCost: z.number(),
    orderTotal: z.number(),
    profit: z.number(),
})
    .meta({
    id: 'QuoteDtfPressAndPressResult',
    description: 'DTF Press & Press quote output. `flooredAt: true` means sellPerShirt was raised up to the minimum-ticket floor.',
});
/**
 * DTF Press & Press: FSP prints DTF transfers and presses them onto garments.
 * Pricing is margin-driven (not rate-per-inch), auto-tiered by qty via `getDTFPPMargin`
 * with caller override optional.
 *
 * Floor semantics (per CLAUDE.md § 1.1): DTFPP's per-shirt floor ($3 above labor+blank)
 * is a minimum ticket size. The engine raises sell up to the floor and sets
 * `flooredAt: true` — it does NOT throw.
 */
export function quoteDtfPressAndPress(rawInput, config) {
    const parsed = QuoteDtfPressAndPressInputSchema.safeParse(rawInput);
    if (!parsed.success) {
        throw new ValidationError(parsed.error.issues);
    }
    const input = parsed.data;
    const cfg = config ?? DEFAULT_PRICING_CONFIG_BODY;
    const dtfpp = cfg.dtfPressAndPress;
    const sizeUpcharge = cfg.universal.sizeUpcharge;
    const sizeAgg = applySizeUpcharges(input.sizes, sizeUpcharge);
    const totalQty = sizeAgg.totalQty;
    const blankCostEach = input.garment.source === 'we_provide' ? input.garment.blankCostEach : 0;
    const avgBlank = (blankCostEach * totalQty + sizeAgg.totalUpcharge) / totalQty;
    const totalSqInPerShirt = input.locations.reduce((sum, loc) => sum + loc.sqin, 0);
    const numLocations = input.locations.length;
    const laborPerLocation = input.laborPerLocation ?? dtfpp.laborPerLocationDefault;
    const laborCostPerShirt = laborPerLocation * numLocations;
    const transferCostPerShirt = totalSqInPerShirt * dtfpp.transferCostPerSqIn;
    const costPerShirt = transferCostPerShirt + laborCostPerShirt + avgBlank;
    const marginPctUsed = input.marginPctOverride ?? getDTFPPMargin(totalQty, dtfpp.marginTiers);
    const marginTargetSell = costPerShirt / (1 - marginPctUsed / 100);
    const floorPerShirt = dtfpp.minSellBeforeLaborBlank + laborCostPerShirt + avgBlank;
    const flooredAt = marginTargetSell < floorPerShirt;
    const sellPerShirt = flooredAt ? floorPerShirt : marginTargetSell;
    const marginPctActual = sellPerShirt > 0 ? ((sellPerShirt - costPerShirt) / sellPerShirt) * 100 : 0;
    const orderCost = costPerShirt * totalQty;
    const orderTotal = sellPerShirt * totalQty;
    const profit = orderTotal - orderCost;
    return {
        totalQty,
        numLocations,
        totalSqInPerShirt,
        blankCostPerUnit: round2(avgBlank),
        sizeUpchargeTotal: sizeAgg.totalUpcharge,
        laborPerLocation,
        laborCostPerShirt: round2(laborCostPerShirt),
        transferCostPerShirt: round2(transferCostPerShirt),
        costPerShirt: round2(costPerShirt),
        marginPctUsed,
        sellPerShirt: round2(sellPerShirt),
        floorPerShirt: round2(floorPerShirt),
        flooredAt,
        marginPctActual: round2(marginPctActual),
        orderCost: round2(orderCost),
        orderTotal: round2(orderTotal),
        profit: round2(profit),
    };
}
//# sourceMappingURL=quote-dtf-press-and-press.js.map